'use client'

import { motion } from 'framer-motion'
import ImageWithFallback from 'src/components/ImageWithFallBack'
import { bookDemo } from 'src/utils'

const FeatureSection = () => {
  const features = [
    {
      title: 'Live in hours, not weeks.',
      label: 'Time to Value',
      description:
        'Get real insights from day one—no long onboarding, no complex setup.',
      image: '/v2/images/niddle.svg'
    },
    {
      title: 'Surface what matters. Act faster.',
      label: 'AI-Powered Insights',
      description:
        'Turn data into insights—spot what matters in marketing, operations, and finance.',
      image: '/v2/images/abstract.svg'
    },
    {
      title: 'Connects with what you already use.',
      label: 'Integration Suite',
      description:
        'Loop plugs into your POS, accounting, and delivery platforms seamlessly.',
      image: '/v2/images/brands.svg'
    },
    {
      title: 'Built for trust. Compliant by default.',
      label: 'Security & Compliance',
      description: 'SOC 2-ready with role-based access and data safeguards.',
      image: '/v2/images/compliance.svg'
    }
  ]

  return (
    <section
      className="py-10 sm:py-24 bg-white relative"
      data-item="feature-section">
      <div className="container mx-auto px-4 sm:px-8">
        <motion.div
          className="text-center mb-10 sm:mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}>
          <h2 className="text-2xl sm:text-5xl font-bold text-[#0a1734] mb-4">
            Built by restaurant operators <br className="hidden sm:block" />{' '}
            {''} for restaurant operators
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-sm sm:text-base">
            Loop helps you move fast, stay compliant, and fix what's broken —
            before it costs you.
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            className="mt-6 sm:mt-8 bg-black text-white px-6 sm:px-8 py-3 sm:py-4 rounded-md font-medium transition-all text-sm sm:text-base"
            onClick={() => bookDemo('feature-section')}>
            Book a demo →
          </motion.button>
        </motion.div>

        <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-0">
          {features.map((feature, i) => (
            <motion.div
              key={i}
              className="bg-white rounded-2xl p-4 sm:p-8 outline outline-1 outline-gray-200"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.1 }}>
              <div className="rounded-xl overflow-hidden">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  transition
                  className="h-32 sm:h-48 mb-4 sm:mb-6 bg-gray-50">
                  <ImageWithFallback
                    src={feature.image}
                    alt={feature.title}
                    width={400}
                    height={300}
                    className="w-full h-full object-cover"
                  />
                </motion.div>
              </div>
              <div className="text-xs sm:text-sm text-gray-500 mb-1 sm:mb-2">
                {feature.label}
              </div>
              <h3 className="text-lg sm:text-2xl font-semibold text-[#0a1734] mb-2 sm:mb-3">
                {feature.title}
              </h3>
              <p className="text-gray-600 text-sm sm:text-base">
                {feature.description}
              </p>
            </motion.div>
          ))}

          {/* Center Logo - only visible on desktop */}
          <motion.div
            className="absolute left-1/2 top-1/2 hidden sm:block bg-white"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.6 }}>
            <div className="w-24 sm:w-26 h-24 sm:h-26 rounded-xl shadow-lg flex items-center justify-center transform -translate-x-1/2 -translate-y-1/2 rotate-45 p-1 bg-white">
              <ImageWithFallback
                src="/images/logos/loop_center.svg"
                alt="Loop Logo"
                width={48}
                height={48}
                className="w-10 sm:w-12 h-10 sm:h-12 bg-gray -rotate-45"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export default FeatureSection
